import { Routes, Route } from "react-router-dom";
import type { ReactElement } from "react";
import type { AuthenticatedUser } from "../api/auth";
import { AIpad } from "../components/AIpad";
import type { LaunchpadTile } from "../components/Launchpad";
import { getGlobalRoutes } from "./globalApplications";
import { ConsolePage } from "../pages/ConsolePage";

interface TenantRoute {
  path: string;
  element: ReactElement;
}

interface TenantContainerProps {
  tiles: LaunchpadTile[];
  routes: TenantRoute[];
  currentUser?: AuthenticatedUser;
}

/**
 * TenantContainer - Unified container for all tenants
 * 
 * IMPORTANT: This is the ONLY place where iframe detection and routing logic exists.
 * All tenants (68e43b18e0652b6ee86a59d6, 69031406f09fed7ea2d97585, 6911e4b63ac6f36785d954a7, FallbackTenant)
 * MUST use this container to ensure consistent behavior.
 * 
 * Architecture:
 * - All tenants run the same core AIpad application
 * - Iframe detection is centralized here (window.self !== window.top)
 * - Global routes are automatically included from globalApplications
 * 
 * Modes:
 * 
 * 1. Iframe Mode (opened from AIpad tile):
 *    - Renders only the application content without AIpad wrapper
 *    - Allows clean embedding of tenant applications in iframe cards
 *    - Used when clicking on tiles in AIpad
 * 
 * 2. Normal Mode (direct access):
 *    - Wraps content in AIpad with full navigation
 *    - Provides complete tenant experience with home button, menu, etc.
 *    - Used when accessing tenant URL directly
 * 
 * Usage in tenant index.tsx:
 * ```tsx
 * export function TenantRoot({ currentUser }: TenantRootProps) {
 *   return (
 *     <TenantContainer
 *       tiles={[...getGlobalLaunchpadTiles({ currentUser }), ...TENANT_TILES]}
 *       routes={TENANT_ROUTES}
 *       currentUser={currentUser}
 *     />
 *   );
 * }
 * ```
 */
export function TenantContainer({ tiles, routes, currentUser }: TenantContainerProps) {
  // Detect if running inside an iframe (AIpad tile)
  const isInIframe = window.self !== window.top;

  // Get global routes (shared across all tenants)
  const globalRoutes = getGlobalRoutes({ currentUser });

  // If in iframe, render only the application without AIpad wrapper
  if (isInIframe) {
    return (
      <Routes>
        <Route path="/" element={<div />} />
        <Route path="/console" element={<ConsolePage />} />
        {routes.map((route) => (
          <Route key={route.path} path={route.path} element={route.element} />
        ))}
        {globalRoutes.map((route) => (
          <Route key={route.path} path={route.path} element={route.element} />
        ))}
        <Route path="*" element={<div />} />
      </Routes>
    );
  }

  // Normal mode: wrap in AIpad (except console which needs to be outside)
  return (
    <Routes>
      {/* Console route OUTSIDE of AIpad wrapper for full-screen overlay */}
      <Route path="/console" element={<ConsolePage />} />

      {/* All other routes wrapped in AIpad */}
      <Route path="*" element={
        <AIpad tiles={tiles}>
          <Routes>
            <Route path="/" element={<div />} />
            {routes.map((route) => (
              <Route key={route.path} path={route.path} element={route.element} />
            ))}
            {globalRoutes.map((route) => (
              <Route key={route.path} path={route.path} element={route.element} />
            ))}
            <Route path="*" element={<div />} />
          </Routes>
        </AIpad>
      } />
    </Routes>
  );
}
